import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CalendarPlus, CalendarDays, CalendarCheck, AlertCircle, X } from 'lucide-react'
import { PageHeader, StatusBadge, DateValue, EmptyState, ErrorState, ConfirmDialog } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { toApiProblem } from '@/lib/api/client'
import { problemMessage } from '@/lib/api/problem'
import { useLeaveTypes, useMyBalances, useMyRequests, useLeaveMutations } from '../api'
import type { RequestView } from '../types'
import { RejoinDialog } from '../components/RejoinDialog'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

/**
 * The form mirrors the server's rules so an application that could never be granted is
 * caught before it reaches an approver's inbox. The server re-checks everything — this is
 * for speed of feedback, not for correctness.
 */
const applySchema = z
  .object({
    leaveTypeId: z.string().min(1, 'Choose a leave type'),
    fromDate: z.string().min(1, 'Choose a start date'),
    toDate: z.string().min(1, 'Choose an end date'),
    dayPart: z.enum(['FULL_DAY', 'FIRST_HALF', 'SECOND_HALF']),
    reason: z.string().max(1000).optional(),
  })
  .refine((values) => values.toDate >= values.fromDate, {
    message: 'The end date cannot be before the start date',
    path: ['toDate'],
  })
  .refine((values) => values.dayPart === 'FULL_DAY' || values.fromDate === values.toDate, {
    message: 'A half day applies to a single date',
    path: ['dayPart'],
  })

type ApplyValues = z.infer<typeof applySchema>

export function LeavePage() {
  const types = useLeaveTypes()
  const balances = useMyBalances()
  const requests = useMyRequests()
  const { apply, cancel } = useLeaveMutations()

  const [showForm, setShowForm] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [toCancel, setToCancel] = useState<RequestView | null>(null)
  const [rejoining, setRejoining] = useState<RequestView | null>(null)

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ApplyValues>({
    resolver: zodResolver(applySchema),
    defaultValues: { dayPart: 'FULL_DAY' },
  })

  async function onSubmit(values: ApplyValues) {
    setServerError(null)
    try {
      await apply.mutateAsync(values)
      reset({ dayPart: 'FULL_DAY' })
      setShowForm(false)
    } catch (error) {
      setServerError(problemMessage(toApiProblem(error) ?? error, 'Could not submit the request'))
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Leave"
        subtitle="Your balances, applications and history"
        actions={
          <Button onClick={() => setShowForm((open) => !open)}>
            <CalendarPlus aria-hidden="true" />
            {showForm ? 'Cancel' : 'Apply for leave'}
          </Button>
        }
      />

      {/* ---------- balances ---------- */}
      {balances.isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-[104px] rounded-[var(--radius-card)]" />
          ))}
        </div>
      )}
      {balances.error != null && <ErrorState error={balances.error} onRetry={() => void balances.refetch()} />}

      {balances.data && balances.data.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {balances.data.map((balance) => (
            <Card key={balance.leaveTypeId} className="p-4">
              <p className="truncate text-xs font-medium text-[var(--color-body)]">{balance.leaveTypeName}</p>
              {balance.unbounded ? (
                <>
                  {/*
                    A type that keeps no balance has nothing to count down. Showing the
                    subtraction anyway reads "−3 of 0" after three days of unpaid leave — a
                    limit reported where there is none.
                  */}
                  <p className="mt-1.5 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
                    {Number(balance.used)}
                    <span className="ms-1 text-sm font-normal text-[var(--color-muted)]">taken</span>
                  </p>
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    No balance is kept
                    {Number(balance.pending) > 0 && ` · ${Number(balance.pending)} awaiting approval`}
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-1.5 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
                    {Number(balance.available)}
                    <span className="ms-1 text-sm font-normal text-[var(--color-muted)]">
                      of {Number(balance.entitled)}
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    {Number(balance.used)} taken
                    {Number(balance.pending) > 0 && ` · ${Number(balance.pending)} awaiting approval`}
                  </p>
                </>
              )}
            </Card>
          ))}
        </div>
      )}

      {balances.data && balances.data.length === 0 && (
        <Card>
          <EmptyState
            title="No leave entitlement yet"
            description="Balances appear once HR grants an entitlement for this year."
          />
        </Card>
      )}

      {/* ---------- apply ---------- */}
      {showForm && (
        <Card className="p-5">
          <h3 className="mb-4 text-base font-semibold text-[var(--color-heading)]">Apply for leave</h3>

          <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} className="space-y-4" noValidate>
            {serverError && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-[var(--radius-control)] bg-[var(--color-tint-red)] px-3 py-2 text-sm text-[var(--color-danger)]"
              >
                <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>{serverError}</span>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="leaveTypeId" required>
                  Leave type
                </Label>
                <Controller
                  control={control}
                  name="leaveTypeId"
                  render={({ field }) => (
                    <Select
                      id="leaveTypeId"
                      required
                      name={field.name}
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(event.target.value)}
                      className="w-full"
                    >
                      <option value="">Choose…</option>
                      {types.data?.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                          {!type.paid && ' (unpaid)'}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                {errors.leaveTypeId && (
                  <p className="text-xs text-[var(--color-danger)]">{errors.leaveTypeId.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dayPart">Duration</Label>
                <Controller
                  control={control}
                  name="dayPart"
                  render={({ field }) => (
                    <Select
                      id="dayPart"
                      name={field.name}
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(event.target.value)}
                      className="w-full"
                    >
                      <option value="FULL_DAY">Full day</option>
                      <option value="FIRST_HALF">First half</option>
                      <option value="SECOND_HALF">Second half</option>
                    </Select>
                  )}
                />
                {errors.dayPart && <p className="text-xs text-[var(--color-danger)]">{errors.dayPart.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="fromDate" required>
                  From
                </Label>
                <Controller
                  control={control}
                  name="fromDate"
                  render={({ field }) => (
                    <DatePicker
                      id="fromDate"
                      required
                      name={field.name}
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(event.target.value)}
                      onBlur={field.onBlur}
                    />
                  )}
                />
                {errors.fromDate && <p className="text-xs text-[var(--color-danger)]">{errors.fromDate.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="toDate" required>
                  To
                </Label>
                <Controller
                  control={control}
                  name="toDate"
                  render={({ field }) => (
                    <DatePicker
                      id="toDate"
                      required
                      name={field.name}
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(event.target.value)}
                      onBlur={field.onBlur}
                    />
                  )}
                />
                {errors.toDate && <p className="text-xs text-[var(--color-danger)]">{errors.toDate.message}</p>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="reason">Reason</Label>
              <textarea
                id="reason"
                rows={3}
                {...register('reason')}
                className="w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-sm"
                placeholder="Anything your approver should know"
              />
            </div>

            <p className="text-xs text-[var(--color-muted)]">
              Weekends and holidays inside the range are not deducted from your balance.
            </p>

            <Button type="submit" disabled={isSubmitting || apply.isPending}>
              {isSubmitting || apply.isPending ? 'Submitting…' : 'Submit request'}
            </Button>
          </form>
        </Card>
      )}

      {/* ---------- history ---------- */}
      <Card className="overflow-hidden">
        <div className="border-b border-[var(--color-border)] px-4 py-3">
          <h3 className="text-sm font-semibold text-[var(--color-heading)]">My requests</h3>
        </div>

        {requests.isLoading && (
          <div className="space-y-2 p-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        )}
        {requests.error != null && <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />}
        {requests.data?.length === 0 && (
          <EmptyState
            icon={CalendarDays}
            title="No requests yet"
            description="Applications you submit appear here with their approval status."
          />
        )}

        <ul className="divide-y divide-[var(--color-border)]">
          {requests.data?.map((request) => (
            <li key={request.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-[var(--color-heading)]">
                  {request.leaveTypeName} · {Number(request.days)} day
                  {Number(request.days) === 1 ? '' : 's'}
                </span>
                <span className="block truncate text-xs text-[var(--color-muted)]">
                  <DateValue value={request.confirmedFromDate ?? request.fromDate} /> –{' '}
                  <DateValue value={request.confirmedToDate ?? request.toDate} />
                  {/* The confirmed dates win once a return is recorded: what was applied for
                      is not what gets paid when somebody comes back early. */}
                  {request.confirmedToDate && request.confirmedToDate !== request.toDate && (
                    <> · returned early</>
                  )}
                  {request.reason && ` · ${request.reason}`}
                </span>
              </span>

              <StatusBadge status={request.status} />

              {request.rejoinStatus === 'AWAITING' && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setRejoining(request)}
                  aria-label={`Confirm return from ${request.leaveTypeName}`}
                >
                  <CalendarCheck aria-hidden="true" />
                  Confirm return
                </Button>
              )}

              {(request.status === 'PENDING' || request.status === 'APPROVED') && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setToCancel(request)}
                  aria-label={`Cancel ${request.leaveTypeName} request`}
                >
                  <X aria-hidden="true" />
                  Cancel
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Card>

      {rejoining && (
        <Card className="p-5">
          <RejoinDialog
            request={rejoining}
            onConfirmed={() => setRejoining(null)}
            onCancel={() => setRejoining(null)}
          />
        </Card>
      )}

      <ConfirmDialog
        open={toCancel !== null}
        onOpenChange={(open) => !open && setToCancel(null)}
        title="Withdraw this request?"
        description={
          toCancel
            ? `${toCancel.leaveTypeName}, ${Number(toCancel.days)} day(s). The days return to your balance.`
            : undefined
        }
        confirmLabel="Withdraw"
        destructive
        loading={cancel.isPending}
        onConfirm={() => {
          if (toCancel) {
            cancel.mutate({ id: toCancel.id }, { onSettled: () => setToCancel(null) })
          }
        }}
      />
    </div>
  )
}
